import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
interface DraggableGridProps {
  items: Array<{
    id: string;
    component: React.ReactNode;
  }>;
  storageKey: string;
  columns?: number;
}
type CardSize = 'small' | 'medium' | 'large';
interface ItemWithSize {
  id: string;
  component: React.ReactNode;
  size: CardSize;
}
export function DraggableGrid({
  items,
  storageKey,
  columns = 3
}: DraggableGridProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [orderedItems, setOrderedItems] = useState<ItemWithSize[]>(items.map(item => ({
    ...item,
    size: 'small' as CardSize
  })));
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  useEffect(() => {
    const savedOrder = localStorage.getItem(storageKey);
    const savedSizes = localStorage.getItem(`${storageKey}-sizes`);
    let sizes: Record<string, CardSize> = {};
    if (savedSizes) {
      try {
        sizes = JSON.parse(savedSizes);
      } catch (e) {
        // Ignore parse errors
      }
    }
    if (savedOrder) {
      try {
        const orderIds = JSON.parse(savedOrder);
        const reordered = orderIds.map((id: string) => {
          const item = items.find(item => item.id === id);
          return item ? {
            ...item,
            size: sizes[id] || 'small'
          } : null;
        }).filter(Boolean) as ItemWithSize[];
        const newItems = items.filter(item => !orderIds.includes(item.id)).map(item => ({
          ...item,
          size: sizes[item.id] || 'small' as CardSize
        }));
        setOrderedItems([...reordered, ...newItems]);
      } catch (e) {
        setOrderedItems(items.map(item => ({
          ...item,
          size: sizes[item.id] || 'small' as CardSize
        })));
      }
    } else {
      setOrderedItems(items.map(item => ({
        ...item,
        size: sizes[item.id] || 'small' as CardSize
      })));
    }
  }, [items, storageKey]);
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      setOrderedItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem(storageKey, JSON.stringify(newOrder.map(item => item.id)));
        return newOrder;
      });
    }
    setActiveId(null);
  };
  const handleDragCancel = () => {
    setActiveId(null);
  };
  const handleSizeChange = (id: string, newSize: CardSize) => {
    setOrderedItems(items => {
      const newItems = items.map(item => item.id === id ? {
        ...item,
        size: newSize
      } : item);
      const sizes = newItems.reduce((acc, item) => {
        acc[item.id] = item.size;
        return acc;
      }, {} as Record<string, CardSize>);
      localStorage.setItem(`${storageKey}-sizes`, JSON.stringify(sizes));
      return newItems;
    });
  };
  const activeItem = orderedItems.find(item => item.id === activeId);
  const getSizeClass = (size: CardSize) => {
    switch (size) {
      case 'small':
        return 'col-span-1';
      case 'medium':
        return 'col-span-1 md:col-span-2';
      case 'large':
        return 'col-span-1 md:col-span-2 lg:col-span-3';
      default:
        return 'col-span-1';
    }
  };
  return <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel} data-id="element-1793">
      <SortableContext items={orderedItems.map(item => item.id)} strategy={rectSortingStrategy} data-id="element-1794">
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-6`} data-id="element-1795">
          {orderedItems.map(item => <SortableItem key={item.id} id={item.id} size={item.size} onSizeChange={handleSizeChange} className={getSizeClass(item.size)} data-id="element-1796">
              {item.component}
            </SortableItem>)}
        </div>
      </SortableContext>
      <DragOverlay data-id="element-1797">
        {activeId && activeItem ? <div className="opacity-50 cursor-grabbing" data-id="element-1798">
            {activeItem.component}
          </div> : null}
      </DragOverlay>
    </DndContext>;
}